import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useDisclosure } from "@chakra-ui/react";
import {
	DndContext,
	PointerSensor,
	closestCenter,
	useSensor,
	useSensors,
	type DragEndEvent,
} from "@dnd-kit/core";
import {
	SortableContext,
	useSortable,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FiPlus, FiTrash2, FiUser } from "react-icons/fi";
import { MdDragIndicator } from "react-icons/md";

import { Button } from "newui/components/Button";
import { useUnitsAndMeasures } from "newui/store";
import { cn } from "utils/helpers";
import { formatCurrency } from "../../../components/QuoteTable/utils";
import { DeleteRowModal } from "../../../components/QuoteTable/DeleteRowModal";
import type { QuoteTableColumn, SupplierRow } from "../../../components/QuoteTable/quote-table.types";
import { CELL_BORDER, COLUMN_STYLE } from "../../../components/QuoteTable/quote-table.types";
import {
	batchDeleteSupplierRows,
	batchSaveSupplierRows,
} from "../../api";
import type { BatchSaveRowPayload } from "../../api";
import { CompactTextInput } from "newui/components/ui/InputWithLabel";
import { computeRowTotal, rowHasContent } from "./BidderQuoteTab.helpers";

export interface SupplierRowsSectionHandle {
	validate: () => boolean;
	/**
	 * Persist any pending supplier rows (rows still living only in local state
	 * because the description-textarea blur hasn't fired). Parents MUST await
	 * this before submitting the quote form, otherwise unsaved rows are lost
	 * on refresh — the click on a submit button races the blur handler.
	 */
	flushPending: () => Promise<void>;
}

export interface SupplierRowsSectionProps {
	columns?: QuoteTableColumn[];
	disabled: boolean;
	/**
	 * Resolved `isAutosaveAllowed(status)` for the current invite. Enforced inside
	 * the save functions themselves, not only at the call site, so a status flip
	 * mid-debounce — or any future caller — cannot leak a write.
	 */
	isAutosaveAllowed: boolean;
	initialRows?: SupplierRow[];
	inviteId: number;
	isClientView: boolean;
	quoteId: number;
	showZeroColumn?: boolean;
	startIndex: number;
	onFormikFieldChange?: (itemId: number, field: string, value: number | string) => void;
	onRowsChange?: (rows: SupplierRow[]) => void;
}

const SUPPLIER_ROW_BG =
	"bg-blue-100/30 group-hover:bg-blue-200 group-focus-within:bg-blue-300";

/**
 * Build the cells payload for a supplier-created row — the primary
 * description (always sent, even if empty) plus a cell for every other
 * column the supplier has a value for. Response cells (rate / qty / etc.)
 * are sent here too, not left to the answer-submission path: that path is
 * keyed off a Formik value written asynchronously by `setFieldValue`, which
 * is not guaranteed to land before `saveHandler` captures its `values`
 * closure, so relying on it silently drops data on newly created rows.
 * Cells-first removes that race.
 */
const buildSupplierRowCells = (
	row: SupplierRow,
	sortedColumns: QuoteTableColumn[],
	primaryDescriptionColId: number | null,
): { column_id: number; value: string | null }[] => {
	const cells: { column_id: number; value: string | null }[] = [];
	if (primaryDescriptionColId !== null) {
		cells.push({ column_id: primaryDescriptionColId, value: row.description ?? "" });
	}
	for (const col of sortedColumns) {
		if (col.id === primaryDescriptionColId) {
			continue; // already pushed above
		}
		const val = row.columnValues?.[col.id];
		if (val === undefined) {
			continue; // never touched — don't create an empty cell
		}
		cells.push({ column_id: col.id, value: String(val) });
	}
	return cells;
};

// ── Sortable row wrapper (needs useSortable hook per row) ──────────
interface SortableSupplierRowProps {
	row: SupplierRow;
	idx: number;
	startIndex: number;
	showZeroColumn: boolean;
	showCheckbox: boolean;
	isSelected: boolean;
	isDragDisabled: boolean;
	sortedColumns: QuoteTableColumn[];
	lastColumnId: number | null;
	disabled: boolean;
	isClientView: boolean;
	onToggleSelect: () => void;
	onDelete: () => void;
	renderSupplierCell: (row: SupplierRow, col: QuoteTableColumn) => React.ReactNode;
}

const SortableSupplierRow: React.FC<SortableSupplierRowProps> = ({
	row,
	idx,
	startIndex,
	showZeroColumn,
	showCheckbox,
	isSelected,
	isDragDisabled,
	sortedColumns,
	lastColumnId,
	disabled,
	isClientView,
	onToggleSelect,
	onDelete,
	renderSupplierCell,
}) => {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id: row.localId, disabled: isDragDisabled });

	const style: React.CSSProperties = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
	};

	return (
		<tr
			ref={setNodeRef}
			style={style}
			className={cn(
				"group border-b border-[#EAECF0]",
				SUPPLIER_ROW_BG,
				isSelected && "!bg-blue-100/60"
			)}
			data-testid={`supplier-row-${row.localId}`}
		>
			{showZeroColumn && (
				<td className={cn(CELL_BORDER, SUPPLIER_ROW_BG)} style={{ width: "5%" }} />
			)}
			<td
				className={cn(
					CELL_BORDER,
					"text-center font-roboto text-xs font-normal text-primary-500",
					SUPPLIER_ROW_BG
				)}
				style={{ height: "35px" }}
			>
				<div className="flex items-center justify-center gap-1">
					{!isDragDisabled && (
						<button
							type="button"
							className="cursor-grab text-gray-400 hover:text-primary-500 active:cursor-grabbing"
							aria-label={`Drag row ${startIndex + idx + 1}`}
							data-testid={`supplier-drag-row-${row.localId}`}
							{...attributes}
							{...listeners}
						>
							<MdDragIndicator size={14} />
						</button>
					)}
					{showCheckbox && (
						<input
							type="checkbox"
							className="h-3.5 w-3.5 cursor-pointer accent-blue-600"
							checked={isSelected}
							onChange={onToggleSelect}
							data-testid={`supplier-row-select-${row.localId}`}
						/>
					)}
					<span>{startIndex + idx + 1}</span>
					<FiUser
						className="text-blue-500"
						size={10}
						title="Supplier-added row"
					/>
				</div>
			</td>
			{sortedColumns.map((col) => (
				<td
					key={col.id}
					className={cn(
						CELL_BORDER,
						COLUMN_STYLE,
						SUPPLIER_ROW_BG
					)}
				>
					<div className="flex items-center justify-between">
						<div className="flex-1">
							{renderSupplierCell(row, col)}
						</div>
						{col.id === lastColumnId && !disabled && !isClientView && (
							<button
								type="button"
								className="ml-2 shrink-0 rounded p-1 text-red-400 hover:bg-red-50 hover:text-red-600"
								onClick={onDelete}
								title="Remove row"
								data-testid={`supplier-row-delete-${row.localId}`}
							>
								<FiTrash2 size={14} />
							</button>
						)}
					</div>
				</td>
			))}
		</tr>
	);
};

const SupplierRowsSection = forwardRef<SupplierRowsSectionHandle, SupplierRowsSectionProps>(({
	columns = [],
	disabled,
	isAutosaveAllowed,
	inviteId,
	isClientView,
	quoteId,
	initialRows = [],
	onRowsChange,
	onFormikFieldChange,
	showZeroColumn = false,
	startIndex,
}, ref) => {
	const nextLocalIdRef = useRef(
		initialRows.reduce((max, row) => {
			const match = row.localId.match(/^supplier-row-(\d+)$/);
			return match ? Math.max(max, Number(match[1])) : max;
		}, 0) + 1
	);
	const getNextLocalId = (): string => {
		const id = `supplier-row-${nextLocalIdRef.current}`;
		nextLocalIdRef.current += 1;
		return id;
	};

	const [rows, setRows] = useState<SupplierRow[]>(initialRows);
	const rowsRef = useRef(rows);
	rowsRef.current = rows;
	const { values: unitsAndMeasures } = useUnitsAndMeasures();
	const { isOpen, onOpen, onClose } = useDisclosure();
	const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
	const [isDeleting, setIsDeleting] = useState(false);
	const [validationErrors, setValidationErrors] = useState<Set<string>>(new Set());
	const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set());
	const [pendingBatchDelete, setPendingBatchDelete] = useState(false);

	const sortedColumns = useMemo(
		() => [...columns].sort((a, b) => a.sortOrder - b.sortOrder),
		[columns]
	);

	const primaryDescriptionColId = useMemo(() => {
		return sortedColumns.find((c) => c.columnType === "description")?.id ?? null;
	}, [sortedColumns]);

	// ── Debounced auto-save ────────────────────────────────────────────
	const DEBOUNCE_MS = 2000;
	const dirtyRowIds = useRef<Set<string>>(new Set());
	const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const isSaving = useRef(false);

	const flushDirtyRows = useCallback(async () => {
		if (!isAutosaveAllowed) {
			dirtyRowIds.current.clear();
			return;
		}
		if (dirtyRowIds.current.size === 0 || isSaving.current) {
			return;
		}
		isSaving.current = true;
		const currentRows = rowsRef.current;
		const dirtyLocalIds = new Set(dirtyRowIds.current);
		dirtyRowIds.current.clear();

		const dirtyRows = currentRows.filter((r) => dirtyLocalIds.has(r.localId));
		// Only save rows that already exist server-side, or that have content
		// somewhere (any column shape — description isn't the only signal).
		const rowsToSave = dirtyRows.filter((r) => r.savedId || rowHasContent(r));
		if (rowsToSave.length === 0) {
			isSaving.current = false;
			return;
		}

		const payload: BatchSaveRowPayload[] = rowsToSave.map((r) => {
			const sortOrder = currentRows.findIndex((cr) => cr.localId === r.localId);
			return {
				id: r.savedId ?? null,
				unit_id: r.unitId,
				sort_order: sortOrder >= 0 ? sortOrder : undefined,
				cells: buildSupplierRowCells(r, sortedColumns, primaryDescriptionColId),
			};
		});

		try {
			const savedRows = await batchSaveSupplierRows(
				{ inviteId, quoteId },
				payload
			);
			// Map returned IDs back to local rows (order matches request order)
			setRows((prev) => {
				const toSave = [...rowsToSave];
				return prev.map((r) => {
					const idx = toSave.findIndex((s) => s.localId === r.localId);
					if (idx !== -1 && savedRows[idx]) {
						return { ...r, savedId: savedRows[idx].id };
					}
					return r;
				});
			});

			// Push Formik values for newly created rows
			if (onFormikFieldChange) {
				for (let i = 0; i < rowsToSave.length; i++) {
					const row = rowsToSave[i];
					const saved = savedRows[i];
					if (!row.savedId && saved && row.columnValues) {
						for (const col of sortedColumns) {
							const val = row.columnValues[col.id];
							if (val === undefined || val === "") {
								continue;
							}
							if (col.columnType === "response_value") {
								const num = parseFloat(val);
								if (!isNaN(num) && num >= 0) {
									onFormikFieldChange(saved.id, `rate_${col.id}`, num);
								}
							} else if (col.columnType === "response_quantity") {
								const num = parseFloat(val);
								if (!isNaN(num) && num >= 0) {
									onFormikFieldChange(saved.id, "quantity", num);
								}
							} else if (col.columnType === "response_text") {
								onFormikFieldChange(saved.id, `text_${col.id}`, val);
							} else if (col.columnType === "response_number") {
								const num = parseFloat(val);
								if (!isNaN(num) && num >= 0) {
									onFormikFieldChange(saved.id, `number_${col.id}`, num);
								}
							} else if (col.columnType === "saving") {
								const num = parseFloat(val);
								if (!isNaN(num)) {
									onFormikFieldChange(saved.id, `rate_${col.id}`, num);
								}
							}
						}
					}
				}
			}
		} catch {
			// On failure, re-mark rows as dirty so the next debounce retries
			for (const id of dirtyLocalIds) {
				dirtyRowIds.current.add(id);
			}
		} finally {
			isSaving.current = false;
		}
	}, [isAutosaveAllowed, inviteId, quoteId, sortedColumns, primaryDescriptionColId, onFormikFieldChange]);

	const scheduleSave = useCallback((localId: string) => {
		if (!isAutosaveAllowed) {
			return;
		}
		dirtyRowIds.current.add(localId);
		if (debounceTimer.current) {
			clearTimeout(debounceTimer.current);
		}
		debounceTimer.current = setTimeout(() => {
			debounceTimer.current = null;
			flushDirtyRows();
		}, DEBOUNCE_MS);
	}, [isAutosaveAllowed, flushDirtyRows]);

	// Clear timer on unmount
	useEffect(() => {
		return () => {
			if (debounceTimer.current) {
				clearTimeout(debounceTimer.current);
			}
		};
	}, []);

	// Sync parent whenever rows change (skip initial mount — parent already has the initial data)
	const didMount = useRef(false);
	useEffect(() => {
		if (!didMount.current) {
			didMount.current = true;
			return;
		}
		onRowsChange?.(rows);
	}, [rows, onRowsChange]);

	// Total column count for colSpan: zero column + # column + dynamic columns + optional delete action
	const totalColSpan = useMemo(() => {
		return (showZeroColumn ? 1 : 0) + 1 + sortedColumns.length;
	}, [sortedColumns, showZeroColumn]);

	useImperativeHandle(ref, () => ({
		flushPending: async (): Promise<void> => {
			// Cancel any pending debounce and flush immediately — the parent
			// is about to submit the form.
			if (debounceTimer.current) {
				clearTimeout(debounceTimer.current);
				debounceTimer.current = null;
			}
			if (!isAutosaveAllowed) {
				dirtyRowIds.current.clear();
				return;
			}
			// Mark all unsaved rows as dirty so they get included
			for (const row of rowsRef.current) {
				if (!row.savedId && rowHasContent(row)) {
					dirtyRowIds.current.add(row.localId);
				}
			}
			await flushDirtyRows();
		},
		validate: (): boolean => {
			if (rows.length === 0) {
				return true;
			}
			const errors = new Set<string>();
			for (const row of rows) {
				// Validate based on actual columns present
				for (const col of sortedColumns) {
					if (col.columnType === "description" && col.id === primaryDescriptionColId) {
						if (!row.description.trim()) {
							errors.add(`${row.localId}-description`);
						}
					} else if (col.columnType === "unit_of_measurement") {
						if (row.unitId === null || row.unitId === undefined) {
							errors.add(`${row.localId}-unitId`);
						}
					} else if (col.columnType === "response_quantity") {
						const val = row.columnValues?.[col.id];
						if (val === undefined || val === null || String(val).trim() === "") {
							errors.add(`${row.localId}-qty_${col.id}`);
						}
					} else if (col.columnType === "response_value") {
						const val = row.columnValues?.[col.id];
						if (val === undefined || val === null || String(val).trim() === "") {
							errors.add(`${row.localId}-rate_${col.id}`);
						}
					}
				}
			}
			if (errors.size > 0) {
				setValidationErrors(errors);
				return false;
			}
			setValidationErrors(new Set());
			return true;
		},
	}), [isAutosaveAllowed, rows, sortedColumns, primaryDescriptionColId, flushDirtyRows]);

	const handleAddRow = useCallback(() => {
		const newRow: SupplierRow = {
			localId: getNextLocalId(),
			description: "",
			unitId: null,
			quantity: "",
			rate: "",
		};
		setRows((prev) => [...prev, newRow]);
	}, []);

	// ── Drag-and-drop reorder ──────────────────────────────────────────
	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
	);

	const handleDragEnd = useCallback(
		(event: DragEndEvent) => {
			const { active, over } = event;
			if (!over || active.id === over.id) {
				return;
			}
			setRows((prev) => {
				const sourceIndex = prev.findIndex((r) => r.localId === active.id);
				const destIndex = prev.findIndex((r) => r.localId === over.id);
				if (sourceIndex < 0 || destIndex < 0) {
					return prev;
				}
				const updated = [...prev];
				const [moved] = updated.splice(sourceIndex, 1);
				updated.splice(destIndex, 0, moved);
				// Mark all moved rows as dirty so auto-save persists the new order
				for (const row of updated) {
					if (row.savedId) {
						dirtyRowIds.current.add(row.localId);
					}
				}
				scheduleSave(moved.localId);
				return updated;
			});
		},
		[scheduleSave]
	);

	const sortableIds = useMemo(() => rows.map((r) => r.localId), [rows]);

	const handleFieldChange = useCallback(
		(
			localId: string,
			field: keyof SupplierRow,
			value: string | number | null
		) => {
			setRows((prev) =>
				prev.map((row) =>
					row.localId === localId ? { ...row, [field]: value } : row
				)
			);

			if (value && String(value).trim()) {
				setValidationErrors((prev) => {
					const next = new Set(prev);
					next.delete(`${localId}-${field}`);
					return next;
				});
			}
			scheduleSave(localId);
		},
		[scheduleSave]
	);

	const handleColumnValueChange = useCallback(
		(localId: string, columnId: number, value: string) => {
			setRows((prev) =>
				prev.map((row) =>
					row.localId === localId
						? { ...row, columnValues: { ...(row.columnValues ?? {}), [columnId]: value } }
						: row
				)
			);

			// Clear validation error for this field
			setValidationErrors((prev) => {
				const next = new Set(prev);
				for (const key of prev) {
					if (key.startsWith(`${localId}-`) && key.endsWith(`_${columnId}`)) {
						next.delete(key);
					}
				}
				return next;
			});

			// Sync numeric column values to Formik — read from ref so we don't
			// need `rows` in the dependency array (avoids stale closure).
			if (onFormikFieldChange) {
				const row = rowsRef.current.find((r) => r.localId === localId);
				if (row?.savedId) {
					const col = sortedColumns.find((c) => c.id === columnId);
					if (col?.columnType === "response_value") {
						const numericValue = parseFloat(value);
						onFormikFieldChange(
							row.savedId,
							`rate_${columnId}`,
							isNaN(numericValue) || numericValue < 0 ? "" : numericValue
						);
					} else if (col?.columnType === "response_quantity") {
						const numericValue = parseFloat(value);
						onFormikFieldChange(
							row.savedId,
							"quantity",
							isNaN(numericValue) || numericValue < 0 ? "" : numericValue
						);
					} else if (col?.columnType === "response_text") {
						onFormikFieldChange(row.savedId, `text_${columnId}`, value);
					} else if (col?.columnType === "saving") {
						const numericValue = parseFloat(value);
						onFormikFieldChange(
							row.savedId,
							`rate_${columnId}`,
							isNaN(numericValue) ? "" : numericValue
						);
					} else if (col?.columnType === "response_number") {
						const numericValue = parseFloat(value);
						onFormikFieldChange(
							row.savedId,
							`number_${columnId}`,
							isNaN(numericValue) || numericValue < 0 ? "" : numericValue
						);
					}
				}
			}
			scheduleSave(localId);
		},
		[onFormikFieldChange, sortedColumns, scheduleSave]
	);

	const handleDeleteClick = useCallback(
		(localId: string) => {
			setDeleteTarget(localId);
			onOpen();
		},
		[onOpen]
	);

	const handleConfirmDelete = useCallback(async () => {
		if (!deleteTarget) {
			return;
		}

		const row = rowsRef.current.find((r) => r.localId === deleteTarget);
		if (!row) {
			onClose();
			return;
		}

		setIsDeleting(true);
		try {
			if (row.savedId) {
				await batchDeleteSupplierRows(
					{ inviteId, quoteId },
					[row.savedId]
				);
			}
			setRows((prev) => prev.filter((r) => r.localId !== deleteTarget));
		} catch {
			// Error handled by global interceptor
		} finally {
			setIsDeleting(false);
			setDeleteTarget(null);
			onClose();
		}
	}, [deleteTarget, inviteId, quoteId, onClose]);

	const toggleRowSelection = useCallback((localId: string) => {
		setSelectedRowIds((prev) => {
			const next = new Set(prev);
			if (next.has(localId)) {
				next.delete(localId);
			} else {
				next.add(localId);
			}
			return next;
		});
	}, []);

	const toggleSelectAll = useCallback(() => {
		setSelectedRowIds((prev) => {
			if (prev.size === rows.length) {
				return new Set();
			}
			return new Set(rows.map((r) => r.localId));
		});
	}, [rows]);

	const handleConfirmBatchDelete = useCallback(async () => {
		if (selectedRowIds.size === 0) {
			return;
		}

		setIsDeleting(true);
		try {
			const targetRows = rowsRef.current.filter((r) => selectedRowIds.has(r.localId));
			const savedIds = targetRows.filter((r) => r.savedId).map((r) => r.savedId as number);
			if (savedIds.length > 0) {
				await batchDeleteSupplierRows({ inviteId, quoteId }, savedIds);
			}
			setRows((prev) => prev.filter((r) => !selectedRowIds.has(r.localId)));
			setSelectedRowIds(new Set());
		} catch {
			// Error handled by global interceptor
		} finally {
			setIsDeleting(false);
			setPendingBatchDelete(false);
		}
	}, [selectedRowIds, inviteId, quoteId]);

	const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "-") {
			event.preventDefault();
		}
	};

	const getUnitLabel = (unitId: number | null): string => {
		if (unitId === null) {
			return "-";
		}
		return (
			unitsAndMeasures?.find((u) => u.value === unitId)?.label ?? "-"
		);
	};

	const handlePaste = useCallback(
		(event: React.ClipboardEvent<HTMLTextAreaElement>, localId: string, colId: number) => {
			const text = event.clipboardData.getData("text");
			if (!text.includes("\t") && !text.includes("\n")) {
				return; // Single-cell paste — let default happen
			}
			event.preventDefault();

			const matrix = text
				.replace(/\r\n/g, "\n")
				.replace(/\r/g, "\n")
				.split("\n")
				.filter((line, idx, arr) => !(idx === arr.length - 1 && line === ""))
				.map((line) => line.split("\t"));

			if (matrix.length === 0) {
				return;
			}

			const startColIndex = sortedColumns.findIndex((c) => c.id === colId);
			if (startColIndex === -1) {
				return;
			}

			const buildRowValues = (pastedRow: string[]): { description: string; columnValues: Record<number, string> } => {
				let description = "";
				const colVals: Record<number, string> = {};
				for (let dx = 0; dx < pastedRow.length; dx++) {
					const destCol = sortedColumns[startColIndex + dx];
					if (!destCol) {
						break;
					}
					const val = pastedRow[dx];
					if (destCol.columnType === "description" && destCol.id === primaryDescriptionColId) {
						description = val;
					} else {
						colVals[destCol.id] = val;
					}
				}
				return { description, columnValues: colVals };
			};

			setRows((prev) => {
				// Find the index of the pasted-into row
				const pasteIdx = prev.findIndex((r) => r.localId === localId);

				// Helper: is a row considered empty (no description, no column values)?
				const isRowEmpty = (r: SupplierRow): boolean =>
					!r.description.trim() &&
					!r.savedId &&
					(!r.columnValues || Object.values(r.columnValues).every((v) => !v || !String(v).trim()));

				// First pasted line updates the target row
				const firstRowData = buildRowValues(matrix[0]);
				const result = prev.map((r) => {
					if (r.localId === localId) {
						return {
							...r,
							description: firstRowData.description || r.description,
							columnValues: { ...(r.columnValues ?? {}), ...firstRowData.columnValues },
						};
					}
					return r;
				});

				// Remaining pasted lines fill empty rows after the target, or insert new ones
				const extraLines = matrix.slice(1);
				let insertAt = pasteIdx + 1;

				for (const pastedRow of extraLines) {
					const data = buildRowValues(pastedRow);
					const newRow: SupplierRow = {
						localId: getNextLocalId(),
						description: data.description,
						unitId: null,
						quantity: "",
						rate: "",
						columnValues: Object.keys(data.columnValues).length > 0 ? data.columnValues : undefined,
					};

					if (insertAt < result.length && isRowEmpty(result[insertAt])) {
						result[insertAt] = {
							...result[insertAt],
							description: data.description || result[insertAt].description,
							columnValues: { ...(result[insertAt].columnValues ?? {}), ...data.columnValues },
						};
					} else {
						result.splice(insertAt, 0, newRow);
					}
					insertAt += 1;
				}

				return result;
			});
		},
		[sortedColumns, primaryDescriptionColId]
	);

	const renderSupplierCell = (row: SupplierRow, col: QuoteTableColumn): React.ReactNode => {
		const isPrimary = col.id === primaryDescriptionColId;

		if (col.columnType === "description") {
			if (isPrimary) {
				if (disabled || isClientView) {
					return (
						<span className="pl-2 font-roboto text-sm text-gray-900 whitespace-pre-wrap break-words">
							{row.description || "-"}
						</span>
					);
				}
				return (
					<textarea
						className={cn(
							"w-full resize-none overflow-hidden rounded border bg-white px-2 py-1 text-sm",
							validationErrors.has(`${row.localId}-description`) ? "border-red-500" : "border-gray-300"
						)}
						rows={1}
						value={row.description}
						onChange={(e) => {
							e.target.style.height = "auto";
							e.target.style.height = `${e.target.scrollHeight}px`;
							handleFieldChange(row.localId, "description", e.target.value);
						}}
						onFocus={(e) => {
							e.target.style.height = "auto";
							e.target.style.height = `${e.target.scrollHeight}px`;
						}}

						onPaste={(e) => handlePaste(e, row.localId, col.id)}
						placeholder="Enter scope description"
						data-testid={`supplier-row-desc-${row.localId}`}
					/>
				);
			}
			// Additional description columns
			const value = row.columnValues?.[col.id] ?? "";
			if (disabled || isClientView) {
				return <span className="pl-2 font-roboto text-sm text-gray-900 whitespace-pre-wrap break-words">{value || "-"}</span>;
			}
			return (
				<textarea
					className="w-full resize-none overflow-hidden rounded border border-gray-300 bg-white px-2 py-1 text-sm"
					rows={1}
					value={value}
					onChange={(e) => {
						e.target.style.height = "auto";
						e.target.style.height = `${e.target.scrollHeight}px`;
						handleColumnValueChange(row.localId, col.id, e.target.value);
					}}
					onFocus={(e) => {
						e.target.style.height = "auto";
						e.target.style.height = `${e.target.scrollHeight}px`;
					}}
					placeholder="Enter description"
					data-testid={`supplier-desc-col-${col.id}-${row.localId}`}
				/>
			);
		}

		if (col.columnType === "unit_of_measurement") {
			if (disabled || isClientView) {
				return (
					<span className="pl-2 font-roboto text-sm text-gray-900">
						{getUnitLabel(row.unitId)}
					</span>
				);
			}
			return (
				<select
					className={cn(
						"w-full rounded border bg-white px-2 py-1 text-sm",
						validationErrors.has(`${row.localId}-unitId`) ? "border-red-500" : "border-gray-300"
					)}
					value={row.unitId ?? ""}
					onChange={(e) =>
						handleFieldChange(
							row.localId,
							"unitId",
							e.target.value ? Number(e.target.value) : null
						)
					}
					data-testid={`supplier-row-unit-${row.localId}`}
				>
					<option value="">Select unit</option>
					{unitsAndMeasures?.map((unit) => (
						<option key={unit.value} value={unit.value}>
							{unit.label}
						</option>
					))}
				</select>
			);
		}

		if (col.columnType === "response_quantity") {
			const value = row.columnValues?.[col.id] ?? "";
			if (disabled || isClientView) {
				return <span className="pl-2 font-roboto text-sm text-gray-900">{value || "-"}</span>;
			}
			return (
				<CompactTextInput
					type="number"
					hasError={validationErrors.has(`${row.localId}-qty_${col.id}`)}
					value={value}
					onChange={(e) =>
						handleColumnValueChange(row.localId, col.id, e.target.value)
					}
					onKeyDown={handleKeyDown}
					min={0}
					disabled={disabled || isClientView}
					placeholder="0"
					data-testid={`supplier-row-qty-${col.id}-${row.localId}`}
					required
				/>
			);
		}

		if (col.columnType === "response_value") {
			const value = row.columnValues?.[col.id] ?? "";
			if (disabled || isClientView) {
				return <span className="pl-2 font-roboto text-sm text-gray-900">{value ? formatCurrency(Number(value)) : "-"}</span>;
			}
			return (
				<div className="relative flex">
					<span className="absolute left-2 top-1/2 z-10 -translate-y-1/2 text-sm text-gray-500">$</span>
					<CompactTextInput
						type="number"
						value={value}
						onChange={(e) =>
							handleColumnValueChange(row.localId, col.id, e.target.value)
						}
						hasError={validationErrors.has(`${row.localId}-rate_${col.id}`)}
						inputClassName="pl-7"
						onKeyDown={handleKeyDown}
						min={0}
						step={1}
						disabled={disabled || isClientView}
						placeholder="0"
						data-testid={`supplier-row-rate-${col.id}-${row.localId}`}
						required
					/>
				</div>
			);
		}

		if (col.columnType === "saving") {
			// Stored as a negative number (supplier types the positive amount, we
			// negate on commit) to mirror procurer-row behaviour in QuoteTableCell.
			const rawValue = row.columnValues?.[col.id] ?? "";
			const storedNum = rawValue === "" ? null : Number(rawValue);
			if (disabled || isClientView) {
				return (
					<span className="font-roboto text-sm font-semibold text-red-600">
						{storedNum === null || isNaN(storedNum)
							? "-"
							: formatCurrency(storedNum)}
					</span>
				);
			}
			const displayValue =
				storedNum === null || isNaN(storedNum)
					? ""
					: String(Math.abs(storedNum));
			return (
				<div className="relative flex">
					<span className="absolute left-2 top-1/2 z-10 -translate-y-1/2 text-sm text-red-600">
						-$
					</span>
					<CompactTextInput
						type="number"
						inputClassName="pl-7"
						value={displayValue}
						onChange={(e) => {
							const numericValue = parseFloat(e.target.value);
							if (isNaN(numericValue) || numericValue < 0) {
								handleColumnValueChange(row.localId, col.id, "");
								return;
							}
							const negated =
								numericValue === 0 ? "0" : String(-Math.abs(numericValue));
							handleColumnValueChange(row.localId, col.id, negated);
						}}
						hasError={validationErrors.has(`${row.localId}-rate_${col.id}`)}
						onKeyDown={handleKeyDown}
						min={0}
						step={1}
						disabled={disabled || isClientView}
						placeholder="0"
						data-testid={`supplier-row-saving-${col.id}-${row.localId}`}
						required
					/>
				</div>
			);
		}

		if (col.columnType === "response_text") {
			const value = row.columnValues?.[col.id] ?? "";
			if (disabled || isClientView) {
				return <span className="pl-2 font-roboto text-sm text-gray-900 whitespace-pre-wrap break-words">{value || "-"}</span>;
			}
			return (
				<textarea
					className="w-full resize-none overflow-hidden rounded border border-gray-300 bg-white px-2 py-1 text-sm"
					rows={1}
					value={value}
					onChange={(e) => {
						e.target.style.height = "auto";
						e.target.style.height = `${e.target.scrollHeight}px`;
						handleColumnValueChange(row.localId, col.id, e.target.value);
					}}
					onFocus={(e) => {
						e.target.style.height = "auto";
						e.target.style.height = `${e.target.scrollHeight}px`;
					}}
					placeholder="Enter response"
					data-testid={`supplier-resp-col-${col.id}-${row.localId}`}
				/>
			);
		}

		if (col.columnType === "response_number") {
			const value = row.columnValues?.[col.id] ?? "";
			return (
				<CompactTextInput
					type="number"
					className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm"
					value={value}
					onChange={(e) => handleColumnValueChange(row.localId, col.id, e.target.value)}
					onKeyDown={handleKeyDown}
					min={0}
					disabled={disabled || isClientView}
					placeholder="0"
					data-testid={`supplier-num-col-${col.id}-${row.localId}`}
				/>
			);
		}

		if (col.columnType === "total") {
			// Mirrors the standard-row cell (QuoteTableCell.tsx:175-205) and
			// the backend (RfxQuoteResponseTotalService) — response_value AND
			// saving columns, multiplied by quantity only when there is exactly
			// one numeric column. See computeRowTotal for the full rule.
			const total = computeRowTotal(sortedColumns, row.columnValues);
			return (
				<span
					className="block pr-2 text-right font-roboto text-sm text-gray-900"
					data-testid={`supplier-row-total-${row.localId}`}
				>
					{formatCurrency(total)}
				</span>
			);
		}

		return <span className="text-gray-400">-</span>;
	};

	if (isClientView && rows.length === 0) {
		return null;
	}

	// Determine if the last column should contain the delete action
	const lastColumnId = sortedColumns.length > 0 ? sortedColumns[sortedColumns.length - 1].id : null;

	const showCheckbox = !disabled && !isClientView && rows.length > 0;
	const allSelected = rows.length > 0 && selectedRowIds.size === rows.length;

	return (
		<>
			{rows.length > 0 && (
				<DndContext
					sensors={sensors}
					collisionDetection={closestCenter}
					onDragEnd={handleDragEnd}
				>
					<SortableContext
						items={sortableIds}
						strategy={verticalListSortingStrategy}
					>
						<tbody data-testid="supplier-rows-section">
							{rows.map((row, idx) => (
								<SortableSupplierRow
									key={row.localId}
									row={row}
									idx={idx}
									startIndex={startIndex}
									showZeroColumn={showZeroColumn}
									showCheckbox={showCheckbox}
									isSelected={selectedRowIds.has(row.localId)}
									isDragDisabled={disabled || isClientView}
									sortedColumns={sortedColumns}
									lastColumnId={lastColumnId}
									disabled={disabled}
									isClientView={isClientView}
									onToggleSelect={() => toggleRowSelection(row.localId)}
									onDelete={() => handleDeleteClick(row.localId)}
									renderSupplierCell={renderSupplierCell}
								/>
							))}
						</tbody>
					</SortableContext>
				</DndContext>
			)}

			{!disabled && !isClientView && (
				<tfoot>
					<tr>
						<td
							colSpan={totalColSpan}
							className="px-4 py-2"
						>
							<div className="flex items-center gap-3">
								<Button
									size="xs"
									btntype="secondary"
									onClick={handleAddRow}
									data-testid="add-supplier-row-btn"
								>
									<FiPlus className="mr-1" />
									Add Row
								</Button>
								{rows.length > 1 && (
									<>
										<Button
											size="xs"
											btntype="secondary"
											onClick={toggleSelectAll}
											data-testid="select-all-supplier-rows-btn"
										>
											{allSelected ? "Deselect All" : "Select All"}
										</Button>
										{selectedRowIds.size > 0 && (
											<Button
												size="xs"
												btntype="secondary"
												onClick={() => setPendingBatchDelete(true)}
												data-testid="batch-delete-supplier-rows-btn"
												className="!border-red-300 !text-red-600 hover:!bg-red-50"
											>
												<FiTrash2 className="mr-1" />
												Delete Selected ({selectedRowIds.size})
											</Button>
										)}
									</>
								)}
							</div>
						</td>
					</tr>
				</tfoot>
			)}

			<DeleteRowModal
				isOpen={isOpen}
				isDeleting={isDeleting}
				onClose={onClose}
				onConfirm={handleConfirmDelete}
			/>

			<DeleteRowModal
				isOpen={pendingBatchDelete}
				isDeleting={isDeleting}
				onClose={() => setPendingBatchDelete(false)}
				onConfirm={handleConfirmBatchDelete}
				message={`Are you sure you want to delete ${selectedRowIds.size} ${selectedRowIds.size === 1 ? "row" : "rows"}? This action cannot be undone.`}
			/>
		</>
	);
});

SupplierRowsSection.displayName = "SupplierRowsSection";

export { SupplierRowsSection };
